/**
 * BackgroundJobsPanel — Floating panel that tracks jobs initiated by the user.
 *
 * Only shows jobs added via the store's addJob() (from import/evaluate buttons).
 * Polls individual job status every 3s while active.
 * Auto-dismisses completed jobs after 8 seconds.
 *
 * Pattern: same as optim-evaluator's EvaluationJobsPanel.
 */

import React, { useCallback, useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import { useTranslations } from "@/hooks/useTranslations"
import {
	ChevronDown,
	ChevronUp,
	X,
	Loader2,
	CheckCircle2,
	XCircle,
	Ban,
	Clock,
	ExternalLink,
	RotateCcw,
} from "lucide-react"
import { useBackgroundJobsStore, type BackgroundJob, type JobStatus, type JobType } from "@/stores/backgroundJobsStore"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"
import { getAuthHeaders } from "@/lib/apiUtils"
import { resolveI18nString } from "@/lib/locale-format"

const POLL_INTERVAL_MS = 3000
// No auto-dismiss — jobs stay until user manually closes them

// ── Status helpers ──────────────────────────────────────────────────

function StatusIcon({ status }: { status: JobStatus }) {
	switch (status) {
		case "pending":
			return <Clock className="h-4 w-4 text-muted-foreground" />
		case "processing":
			return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
		case "completed":
			return <CheckCircle2 className="h-4 w-4 text-green-500" />
		case "failed":
			return <XCircle className="h-4 w-4 text-red-500" />
		case "cancelled":
			return <Ban className="h-4 w-4 text-muted-foreground" />
		default:
			return null
	}
}

function statusLabel(status: JobStatus, t: ReturnType<typeof useTranslations>): string {
	switch (status) {
		case "pending": return t("backgroundJobs.status.pending")
		case "processing": return t("backgroundJobs.status.processing")
		case "completed": return t("backgroundJobs.status.completed")
		case "failed": return t("backgroundJobs.status.failed")
		case "cancelled": return t("backgroundJobs.status.cancelled")
		default: return status
	}
}

function jobTypeLabel(type: string, t: ReturnType<typeof useTranslations>): string {
	switch (type) {
		case "tender_import": return t("backgroundJobs.import")
		case "evaluation": return t("backgroundJobs.evaluation")
		case "comparison": return t("backgroundJobs.comparison")
		case "pliego_generation": return t("backgroundJobs.pliegoGeneration")
		case "section_extraction": return t("backgroundJobs.sectionExtraction")
		case "specs_analysis": return t("backgroundJobs.specsAnalysis")
		case "template_decision_analysis": return t("backgroundJobs.templateDecisionAnalysis")
		case "document_review": return t("backgroundJobs.documentReview")
		case "report_chat_execute": return t("backgroundJobs.reportChatExecute")
		default: return type
	}
}

// ── Job row ─────────────────────────────────────────────────────────

/** Get a link for a completed job (navigates to result) */
function getJobLink(job: BackgroundJob): string | null {
	// Document review: link back to the editor and auto-open the review panel.
	if (job.type === "document_review") {
		if (job.status !== "completed") return null
		const input = job.input as Record<string, unknown> | null
		const expedientId = input?.expedientId as string | undefined
		return expedientId ? `/expedients/${expedientId}/editor?review=open` : null
	}

	// Section extraction: link directly to the document editor
	if (job.type === "section_extraction" && job.status === "completed") {
		const input = job.input as Record<string, unknown> | null
		const result = job.result as Record<string, unknown> | null
		// Find the reportId from the first extracted document
		const docs = (result?.documents as Array<Record<string, unknown>>) || []
		const reportId = docs[0]?.reportId as string | undefined

		if (input?.targetType === "template" && input?.targetId) {
			return reportId
				? `/procurement-templates/${input.targetId}/editor?doc=${reportId}`
				: `/procurement-templates/${input.targetId}`
		}
		if (input?.targetType === "expedient" && input?.targetId) {
			return reportId
				? `/expedients/${input.targetId}/editor?doc=${reportId}`
				: `/expedients/${input.targetId}`
		}
		return null
	}

	// Model decision analysis: link to the template detail, where the decisions are shown.
	if (job.type === "template_decision_analysis" && job.status === "completed") {
		const input = job.input as Record<string, unknown> | null
		return input?.templateId ? `/procurement-templates/${input.templateId}` : null
	}

	// Pliego generation: completed → expedient, active → tender-assistant session
	if (job.type === "pliego_generation") {
		const result = job.result as Record<string, unknown> | null
		if (job.status === "completed" && result?.expedientId) {
			return `/expedients/${result.expedientId}`
		}
		return null
	}

	// Other job types: only show link on completion
	if (job.status !== "completed" || !job.result) return null
	const result = job.result as Record<string, unknown>

	if (job.type === "tender_import" && result.results) {
		const entries = Object.values(result.results as Record<string, { templateId?: string; expedientId?: string; success: boolean }>)
		// Prefer template link (new flow), fall back to expedient (legacy imports)
		const firstTemplate = entries.find((e) => e.success && e.templateId)
		if (firstTemplate?.templateId) return `/procurement-templates/${firstTemplate.templateId}`
		const firstExpedient = entries.find((e) => e.success && e.expedientId)
		return firstExpedient?.expedientId ? `/expedients/${firstExpedient.expedientId}` : null
	}

	if (job.type === "evaluation" && result.expedientId) {
		const highlight = result.proposalId
			? `proposal-${result.proposalId}`
			: result.lotId
				? `lot-${result.lotId}`
				: null
		return `/expedients/${result.expedientId}?tab=evaluation${highlight ? `&highlight=${highlight}` : ""}`
	}
	if (job.type === "evaluation" && result.evaluationId) {
		return `/tenders` // Fallback for old jobs without expedientId
	}

	if (job.type === "comparison" && result.expedientId) {
		const lotId = result.lotId as string | null
		const highlight = lotId ? `comparison-${lotId}` : "comparison-general"
		return `/expedients/${result.expedientId}?tab=evaluation&highlight=${highlight}`
	}

	return null
}

function JobRow({ job, onCancel, onDismiss, onNavigate, onRetry, isRetrying, router, t }: {
	job: BackgroundJob
	onCancel: (id: string) => void
	onDismiss: (id: string) => void
	onNavigate: (path: string, jobId: string) => void
	onRetry: (id: string) => void
	isRetrying: boolean
	router: ReturnType<typeof useNavigate>
	t: ReturnType<typeof useTranslations>
}) {
	const isActive = job.status === "pending" || job.status === "processing"
	const isTerminal = job.status === "completed" || job.status === "failed" || job.status === "cancelled"
	const RETRYABLE_TYPES = ["tender_import", "evaluation", "comparison", "section_extraction", "pliego_generation"]
	const canRetry = (job.status === "failed" || job.status === "cancelled") && RETRYABLE_TYPES.includes(job.type)
	const link = getJobLink(job)

	// Decode server-encoded i18n sentinels so titles + step labels follow the active locale.
	const localizedTitle = resolveI18nString(job.title ?? "", t) || `Job ${job.id.slice(0, 8)}`
	const localizedStep = resolveI18nString(job.currentStep ?? "", t)
	const localizedError = resolveI18nString(job.errorMessage ?? "", t)

	// Active pliego generation → link to tender-assistant session for live progress
	const activeLink = isActive && job.type === "pliego_generation"
		? (() => {
			// sessionId is in input (always available) or result (after expedient creation)
			const input = job.input as Record<string, unknown> | null
			const result = job.result as Record<string, unknown> | null
			const sessionId = (input?.sessionId ?? result?.sessionId) as string | undefined
			return sessionId ? `/tender-assistant/${sessionId}` : null
		})()
		: null

	return (
		<div className="px-3 py-2.5 border-b border-border/40 last:border-b-0">
			<div className="flex items-start gap-2">
				<div className="mt-0.5 flex-shrink-0">
					<StatusIcon status={job.status} />
				</div>

				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-1.5">
						<span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
							{jobTypeLabel(job.type, t)}
						</span>
						<span className="text-[10px] text-muted-foreground">
							· {statusLabel(job.status, t)}
						</span>
					</div>
					<p className="text-sm font-medium truncate mt-0.5" title={localizedTitle}>
						{localizedTitle}
					</p>
				</div>

				<div className="flex-shrink-0 flex items-center gap-0.5">
					{isActive && (
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onCancel(job.id)} title={t("backgroundJobs.cancel")}>
							<X className="h-3.5 w-3.5" />
						</Button>
					)}
					{isTerminal && (
						<Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDismiss(job.id)} title={t("backgroundJobs.close")}>
							<X className="h-3.5 w-3.5" />
						</Button>
					)}
				</div>
			</div>

			{isActive && (
				<div className="mt-2 ml-6">
					<Progress value={job.progress || 0} className="h-1.5" />
					<div className="flex items-center justify-between mt-1">
						{localizedStep && (
							<p className="text-[11px] text-muted-foreground truncate flex-1">
								{localizedStep}
							</p>
						)}
						{activeLink && (
							<button
								onClick={() => router(activeLink)}
								className="text-[10px] text-blue-500 hover:text-blue-700 font-medium shrink-0 ml-2"
							>
								{t("backgroundJobs.viewProgress")}
							</button>
						)}
					</div>
				</div>
			)}

			{/* Action button when completed */}
			{job.status === "completed" && link && (
				<div className="mt-2 ml-6">
					<Button
						variant="outline"
						size="sm"
						className="h-7 text-xs gap-1.5"
						onClick={() => onNavigate(link, job.id)}
					>
						<ExternalLink className="h-3 w-3" />
						{job.type === "comparison"
						? t("backgroundJobs.viewComparison")
						: job.type === "pliego_generation"
							? t("backgroundJobs.viewPliego")
							: job.type === "template_decision_analysis"
								? t("backgroundJobs.viewTemplate")
							: job.type === "tender_import"
								? t("backgroundJobs.viewTemplate")
								: job.type === "section_extraction"
									? t("backgroundJobs.viewDocument")
									: job.type === "document_review"
											? t("backgroundJobs.viewDocuments")
											: t("backgroundJobs.viewExpedient")}
					</Button>
				</div>
			)}

			{/* Warning for completed import with no template created */}
			{job.status === "completed" && !link && job.type === "tender_import" && (
				<div className="mt-2 ml-6">
					<p className="text-xs text-amber-600">
						{t("backgroundJobs.noTemplateCreated")}
					</p>
				</div>
			)}

			{/* Warning for completed generation with failed sections */}
			{job.status === "completed" && job.type === "pliego_generation" && (() => {
				const failed = (job.result as { failedSections?: string[] } | null)?.failedSections
				return failed && failed.length > 0 ? (
					<div className="mt-2 ml-6">
						<p className="text-xs text-amber-600" title={failed.join(", ")}>
							{t("backgroundJobs.sectionsFailed", { count: failed.length })}
						</p>
					</div>
				) : null
			})()}

			{/* Error message + retry for failed/cancelled jobs */}
			{(job.status === "failed" || job.status === "cancelled") && (
				<div className="mt-2 ml-6">
					{localizedError && (
						<p className="text-xs text-red-500 truncate mb-1.5" title={localizedError}>
							{localizedError}
						</p>
					)}
					{canRetry && (
						<Button
							variant="outline"
							size="sm"
							className="h-7 text-xs gap-1.5"
							onClick={() => onRetry(job.id)}
							disabled={isRetrying}
						>
							{isRetrying ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<RotateCcw className="h-3 w-3" />
							)}
							{isRetrying ? t("backgroundJobs.retrying") : t("backgroundJobs.retry")}
						</Button>
					)}
				</div>
			)}
		</div>
	)
}

// ── Main panel ──────────────────────────────────────────────────────

export function BackgroundJobsPanel() {
	const router = useNavigate()
	const t = useTranslations()
	const { jobs, isMinimized, updateJob, removeJob, toggleMinimized, hasActiveJobs, activeJobCount, addJob, setJobs } =
		useBackgroundJobsStore()
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const initialLoadDone = useRef(false)
	const [retryingIds, setRetryingIds] = React.useState<Set<string>>(new Set())

	// ── Load active jobs from DB on mount (survives page refresh) ──
	useEffect(() => {
		if (initialLoadDone.current) return
		initialLoadDone.current = true

		getAuthHeaders().then((headers) => {
			if (!headers.Authorization) {
				initialLoadDone.current = false
				return
			}
			return fetch("/api/jobs?status=pending,processing", { headers })
				.then((res) => res.ok ? res.json() : { success: false, data: [] })
				.then((data) => {
					if (data.success && Array.isArray(data.data)) {
						const existingIds = new Set(jobs.map((j) => j.id))
						for (const remoteJob of data.data) {
							if (!existingIds.has(remoteJob.id)) {
								addJob({
									id: remoteJob.id,
									type: remoteJob.type || "tender_import",
									status: remoteJob.status || "pending",
									progress: remoteJob.progress || 0,
									currentStep: remoteJob.currentStep || null,
									title: remoteJob.title || `Job ${remoteJob.id.slice(0, 8)}`,
									subtitle: remoteJob.subtitle || null,
									input: remoteJob.input || null,
									result: remoteJob.result || null,
									errorMessage: remoteJob.errorMessage || null,
									createdAt: remoteJob.createdAt || new Date().toISOString(),
									startedAt: remoteJob.startedAt || null,
									completedAt: remoteJob.completedAt || null,
								})
							}
						}
					}
				})
				.catch(() => {
					// Silently ignore — if API fails, we just don't restore jobs
				})
		})
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// ── Periodically discover NEW server-side jobs (e.g. section extraction spawned by tender import) ──
	useEffect(() => {
		const interval = setInterval(async () => {
			const headers = await getAuthHeaders()
			if (!headers.Authorization) return
			fetch("/api/jobs?status=pending,processing", { headers })
				.then(r => r.ok ? r.json() : { success: false, data: [] })
				.then(data => {
					if (!data.success || !Array.isArray(data.data)) return
					const existingIds = new Set(jobs.map(j => j.id))
					for (const rj of data.data) {
						if (!existingIds.has(rj.id)) {
							addJob({
								id: rj.id, type: rj.type || "tender_import", status: rj.status || "pending",
								progress: rj.progress || 0, currentStep: rj.currentStep || null,
								title: rj.title || `Job ${rj.id.slice(0, 8)}`, subtitle: rj.subtitle || null,
								input: rj.input || null, result: rj.result || null, errorMessage: rj.errorMessage || null,
								createdAt: rj.createdAt || new Date().toISOString(), startedAt: rj.startedAt || null, completedAt: null,
							})
						}
					}
				})
				.catch(() => {})
		}, 10000) // Check every 10s
		return () => clearInterval(interval)
	}, [jobs, addJob])

	// ── Poll active jobs individually (recursive setTimeout — never overlaps) ───
	const isPollingRef = useRef(false)
	const hasActive = jobs.some((j) => j.status === "pending" || j.status === "processing")

	useEffect(() => {
		if (!hasActive) {
			// Stop polling
			if (pollRef.current) {
				clearTimeout(pollRef.current)
				pollRef.current = null
			}
			isPollingRef.current = false
			return
		}

		// Already polling — don't start another loop
		if (isPollingRef.current) return
		isPollingRef.current = true

		const poll = async () => {
			// Read current jobs from store (avoids stale closure)
			const currentJobs = useBackgroundJobsStore.getState().jobs
			const activeJobs = currentJobs.filter((j) => j.status === "pending" || j.status === "processing")
			if (activeJobs.length === 0) {
				isPollingRef.current = false
				return
			}

			await Promise.allSettled(
				activeJobs.map(async (job) => {
					try {
						const headers = await getAuthHeaders()
						const res = await fetch(`/api/jobs/${job.id}`, { headers })
						if (!res.ok) {
							if (res.status === 404) {
								updateJob(job.id, {
									status: "failed",
									progress: 0,
									currentStep: null,
									errorMessage: t("backgroundJobs.staleTimeout"),
									completedAt: new Date().toISOString(),
								})
							}
							return
						}
						const data = await res.json()
						if (!data.success || !data.data) return

						const remote = data.data
						updateJob(job.id, {
							status: remote.status,
							progress: remote.progress || 0,
							currentStep: remote.currentStep || null,
							input: remote.input || null,
							result: remote.result,
							errorMessage: remote.errorMessage || null,
							startedAt: remote.startedAt || null,
							completedAt: remote.completedAt || null,
						})
					} catch {
						// Network error — skip, retry next cycle
					}
				})
			)

			// Schedule next poll AFTER all responses complete (no request stacking)
			if (isPollingRef.current) {
				pollRef.current = setTimeout(poll, POLL_INTERVAL_MS)
			}
		}

		// Start first poll immediately
		poll()

		return () => {
			isPollingRef.current = false
			if (pollRef.current) {
				clearTimeout(pollRef.current)
				pollRef.current = null
			}
		}
	}, [hasActive, updateJob])

	// Jobs stay visible until manually dismissed by the user (no auto-dismiss)


	const handleCancel = useCallback(async (id: string) => {
		try {
			const headers = await getAuthHeaders()
			await fetch(`/api/jobs/${id}`, {
				method: "POST",
				headers,
				body: JSON.stringify({ action: "cancel" }),
			})
		} catch {
			// ignore
		}
	}, [])

	const handleDismiss = useCallback(async (id: string) => {
		removeJob(id)
	}, [removeJob])

	const handleRetry = useCallback(async (id: string) => {
		setRetryingIds((prev) => new Set(prev).add(id))
		try {
			const headers = await getAuthHeaders()
			const failedJob = jobs.find((j) => j.id === id)
			const res = await fetch(`/api/jobs/${id}`, {
				method: "POST",
				headers,
				body: JSON.stringify({ action: "retry" }),
			})
			const data = await res.json()
			if (data.success && data.data?.newJobId) {
				const newType = ((data.data.type as JobType | undefined) || failedJob?.type || "tender_import") as JobType
				const defaultTitle = newType === "tender_import"
					? t("backgroundJobs.importTenders", { count: data.data.totalTenders || 1 })
					: (failedJob?.title || `Job ${data.data.newJobId.slice(0, 8)}`)

				removeJob(id)
				addJob({
					id: data.data.newJobId,
					type: newType,
					status: "pending",
					progress: 0,
					currentStep: null,
					title: data.data.title || defaultTitle,
					subtitle: failedJob?.subtitle || null,
					input: null,
					result: null,
					errorMessage: null,
					createdAt: new Date().toISOString(),
					startedAt: null,
					completedAt: null,
				})
			} else if (data.error) {
				// Keep the failed job but surface the validation error
				const message = String(data.error)
				updateJob(id, { errorMessage: message })
			}
		} catch {
			// Network error — ignore
		} finally {
			setRetryingIds((prev) => {
				const next = new Set(prev)
				next.delete(id)
				return next
			})
		}
	}, [jobs, removeJob, addJob, updateJob, t])

	const handleNavigate = useCallback((path: string, jobId: string) => {
		handleDismiss(jobId)
		router(path)

		// Extract highlight target from URL params
		const url = new URL(path, window.location.origin)
		const highlight = url.searchParams.get("highlight")
		if (!highlight) return

		// Scroll + highlight with retry loop (waits for page to render)
		let attempts = 0
		const tryScroll = () => {
			const el = document.getElementById(highlight)
			if (el) {
				el.scrollIntoView({ behavior: "smooth", block: "start" })
				el.classList.add("highlight-review")
				setTimeout(() => el.classList.remove("highlight-review"), 3000)
			} else if (attempts < 25) {
				attempts++
				setTimeout(tryScroll, 200)
			}
		}
		setTimeout(tryScroll, 1500)
	}, [handleDismiss, router])

	// Don't render if no jobs tracked
	if (jobs.length === 0) return null

	const activeCount = activeJobCount()

	return (
		<div className="fixed bottom-4 right-4 z-50 w-80 max-h-[70vh] flex flex-col rounded-xl border border-border/60 bg-card shadow-lg overflow-hidden">
			{/* Header */}
			<div
				className="flex items-center justify-between px-3 py-2 border-b border-border/40 bg-muted/30 cursor-pointer select-none"
				onClick={toggleMinimized}
			>
				<div className="flex items-center gap-2">
					{activeCount > 0 && <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />}
					<span className="text-sm font-medium">
						{activeCount > 0
							? (activeCount === 1
								? t("backgroundJobs.activeJobsOne", { count: activeCount })
								: t("backgroundJobs.activeJobsOther", { count: activeCount }))
							: (jobs.length === 1
								? t("backgroundJobs.totalJobsOne", { count: jobs.length })
								: t("backgroundJobs.totalJobsOther", { count: jobs.length }))
						}
					</span>
				</div>
				{isMinimized ? (
					<ChevronUp className="h-4 w-4 text-muted-foreground" />
				) : (
					<ChevronDown className="h-4 w-4 text-muted-foreground" />
				)}
			</div>

			{/* Job list */}
			{!isMinimized && (
				<div className="flex-1 overflow-y-auto max-h-[60vh]">
					{jobs.map((job) => (
						<JobRow
							key={job.id}
							job={job}
							onCancel={handleCancel}
							onDismiss={handleDismiss}
							onNavigate={handleNavigate}
							onRetry={handleRetry}
							isRetrying={retryingIds.has(job.id)}
							router={router}
							t={t}
						/>
					))}
				</div>
			)}
		</div>
	)
}
